<script setup lang="ts">
import { computed } from 'vue'

export interface TabItem {
  key: string
  label: string
  disabled?: boolean
  badge?: string | number
}

interface Props {
  modelValue: string
  items: TabItem[]
  variant?: 'line' | 'pill'
  size?: 'sm' | 'md'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'line',
  size: 'md',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  change: [value: string]
}>()

const activeKey = computed(() => props.modelValue)

function select(key: string): void {
  if (key === props.modelValue) return
  emit('update:modelValue', key)
  emit('change', key)
}
</script>

<template>
  <div
    class="flex items-center gap-1 overflow-x-auto"
    :class="variant === 'line' ? 'border-b border-border' : 'rounded-md bg-bg-elevated p-1'"
    role="tablist"
  >
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      role="tab"
      :aria-selected="activeKey === item.key"
      :disabled="item.disabled"
      class="relative inline-flex items-center gap-2 font-medium transition-colors duration-fast disabled:cursor-not-allowed disabled:opacity-50"
      :class="[
        size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm',
        variant === 'line'
          ? activeKey === item.key
            ? 'text-brand-600 dark:text-brand-300'
            : 'text-fg-muted hover:text-fg'
          : 'rounded-md ' +
            (activeKey === item.key
              ? 'bg-bg-surface text-fg shadow-sm'
              : 'text-fg-muted hover:text-fg'),
      ]"
      @click="select(item.key)"
    >
      <span>{{ item.label }}</span>
      <span
        v-if="item.badge !== undefined"
        class="ml-1 rounded-full bg-brand-500/15 px-1.5 py-0.5 text-xs font-semibold text-brand-600 dark:text-brand-300"
      >
        {{ item.badge }}
      </span>
      <span
        v-if="variant === 'line' && activeKey === item.key"
        class="absolute -bottom-px left-0 right-0 h-0.5 bg-brand-500"
      />
    </button>
  </div>
</template>
